/**
 * NotificationToast Component
 *
 * Bell icon with unread badge + dropdown panel for real-time drift notifications.
 * Polls for spec notifications (drift alerts, approval changes, scan completions)
 * and displays them in a dropdown overlay.
 *
 * Part of Real-Time Drift Notifications — brand-agnostic platform feature.
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiBell,
  HiExclamationCircle,
  HiCheckCircle,
  HiCheck,
  HiClock,
  HiLink,
} from 'react-icons/hi';
import { HiDocumentText, HiSparkles, HiUserGroup } from 'react-icons/hi2';
import { useStatus } from '../contexts/StatusProvider';
import { useNotifications } from '../hooks/useNotifications';
import type { SpecNotification } from '../api/specifications';

// ---------------------------------------------------------------------------
// Icon + color mapping per notification type
// ---------------------------------------------------------------------------

interface NotificationStyle {
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  bgClass: string;
}

const NOTIFICATION_STYLES: Record<string, NotificationStyle> = {
  drift_detected: {
    icon: HiExclamationCircle,
    colorClass: 'text-yellow-500',
    bgClass: 'bg-yellow-50',
  },
  scan_complete: {
    icon: HiCheckCircle,
    colorClass: 'text-green-500',
    bgClass: 'bg-green-50',
  },
  approval_requested: {
    icon: HiUserGroup,
    colorClass: 'text-blue-500',
    bgClass: 'bg-blue-50',
  },
  approval_submitted: {
    icon: HiCheck,
    colorClass: 'text-green-600',
    bgClass: 'bg-green-50',
  },
  spec_published: {
    icon: HiDocumentText,
    colorClass: 'text-purple-500',
    bgClass: 'bg-purple-50',
  },
  spec_outdated: {
    icon: HiClock,
    colorClass: 'text-orange-500',
    bgClass: 'bg-orange-50',
  },
  embellishment_ready: {
    icon: HiSparkles,
    colorClass: 'text-violet-500',
    bgClass: 'bg-violet-50',
  },
  dependency_change: {
    icon: HiLink,
    colorClass: 'text-gray-500',
    bgClass: 'bg-gray-50',
  },
};

const DEFAULT_STYLE: NotificationStyle = {
  icon: HiBell,
  colorClass: 'text-gray-500',
  bgClass: 'bg-gray-50',
};

// ---------------------------------------------------------------------------
// Severity → border color
// ---------------------------------------------------------------------------

function severityBorderClass(severity: string): string {
  switch (severity) {
    case 'error':
      return 'border-l-red-500';
    case 'warning':
      return 'border-l-yellow-500';
    case 'success':
      return 'border-l-green-500';
    case 'info':
    default:
      return 'border-l-blue-400';
  }
}

// ---------------------------------------------------------------------------
// Relative time helper
// ---------------------------------------------------------------------------

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Single notification row
// ---------------------------------------------------------------------------

interface NotificationRowProps {
  notification: SpecNotification;
  onClick: (n: SpecNotification) => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ notification, onClick }) => {
  const style = NOTIFICATION_STYLES[notification.Notification_Type] || DEFAULT_STYLE;
  const Icon = style.icon;
  const isUnread = notification.Status === 'unread';

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className={`w-full text-left px-4 py-3 border-l-4 ${severityBorderClass(notification.Severity)} ${
        isUnread ? 'bg-white' : 'bg-gray-50 opacity-75'
      } hover:bg-gray-100 transition-colors`}
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 rounded-full p-1.5 ${style.bgClass}`}>
          <Icon className={`w-4 h-4 ${style.colorClass}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'}`}>
              {notification.Title}
            </p>
            {isUnread && (
              <span className="flex-shrink-0 w-2 h-2 rounded-full bg-indigo-500" />
            )}
          </div>
          {notification.Message && (
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.Message}</p>
          )}
          <div className="flex items-center gap-2 mt-1">
            {notification.Spec_Title && (
              <span className="text-xs text-indigo-600 truncate max-w-[180px]">
                {notification.Spec_Title}
              </span>
            )}
            <span className="text-xs text-gray-400">{timeAgo(notification.Created_At)}</span>
          </div>
        </div>
      </div>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const NotificationToast: React.FC = () => {
  const { Client_ID } = useStatus();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications(Client_ID);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (n: SpecNotification) => {
    markAsRead(n.Notification_ID);
    if (n.Spec_ID) {
      setIsOpen(false);
      navigate(`/specs/${n.Spec_ID}`);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative inline-flex items-center p-1.5 text-gray-500 hover:text-gray-700 focus:outline-none"
        aria-label="Notifications"
      >
        <HiBell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-h-[28rem] rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllAsRead()}
                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <HiBell className="mx-auto w-8 h-8 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No notifications</p>
              </div>
            ) : (
              notifications.map(n => (
                <NotificationRow
                  key={n.Notification_ID}
                  notification={n}
                  onClick={handleNotificationClick}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationToast;
